import { z } from "zod";
import { AiActionPolicyV1Schema } from "../ai-action-policy";
import {
  BiddingStrategySchema,
  CalendarDateSchema,
  NegativeMatchTypeSchema,
  PlacementSchema,
} from "../campaign-creation";
import {
  SMART_CREATION_MAX_CAMPAIGNS,
  SmartCreationCampaignDraftContentV2Schema,
  SmartCreationTargetingStrategyKindSchema,
} from "../smart-creation";
import { SmartCreationReviewGraphSchema } from "./graph";
import {
  BidSchema,
  BudgetSchema,
  EntityIdSchema,
  isSmartCreationNegativeKeywordWithinWordLimit,
  NameSchema,
  PlacementPercentageSchema,
  SmartCreationReviewAdGroupRefSchema,
  SmartCreationReviewBudgetAllocationSchema,
  SmartCreationReviewCampaignRefSchema,
  SmartCreationReviewNamingRuleSchema,
  SmartCreationReviewNegativeKeywordRefSchema,
  SmartCreationReviewNegativeProductRefSchema,
  SmartCreationReviewPlanRefSchema,
  SmartCreationReviewTargetRefSchema,
} from "./values";

export const SmartCreationReviewFieldSchema = z.enum([
  "plan_name",
  "portfolio_id",
  "campaign_name",
  "budget_allocation",
  "bidding_strategy",
  "placement",
  "excluded",
  "ad_group_name",
  "default_bid",
  "target_bid",
  "target_match_type",
  "target_intent",
  "negative_value",
  "negative_match_type",
]);
export type SmartCreationReviewField = z.infer<typeof SmartCreationReviewFieldSchema>;

const NegativeRefSchema = z.discriminatedUnion("kind", [
  SmartCreationReviewNegativeKeywordRefSchema,
  SmartCreationReviewNegativeProductRefSchema,
]);

const SmartCreationReviewTargetCreateSchema = z.discriminatedUnion("type", [
  z
    .object({
      type: z.literal("keyword"),
      ref: SmartCreationReviewTargetRefSchema,
      sourceTargetId: EntityIdSchema,
      strategyKind: SmartCreationTargetingStrategyKindSchema.exclude(["automatic"]),
      keyword: z.string().trim().min(1).max(80),
      matchType: z.enum(["broad", "phrase", "exact"]),
      bid: BidSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal("product"),
      matchType: z.enum(["exact", "expanded"]).optional(),
      ref: SmartCreationReviewTargetRefSchema,
      sourceTargetId: EntityIdSchema,
      strategyKind: SmartCreationTargetingStrategyKindSchema.exclude(["automatic"]),
      asin: z
        .string()
        .trim()
        .regex(/^[A-Z0-9]{10}$/),
      bid: BidSchema,
    })
    .strict(),
]);

const SmartCreationReviewNegativeCreateSchema = z.discriminatedUnion("type", [
  z
    .object({
      type: z.literal("keyword"),
      ref: SmartCreationReviewNegativeKeywordRefSchema,
      text: z.string().trim().min(1).max(80),
      matchType: NegativeMatchTypeSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal("product"),
      ref: SmartCreationReviewNegativeProductRefSchema,
      asin: z
        .string()
        .trim()
        .regex(/^[A-Z0-9]{10}$/),
    })
    .strict(),
]);

const SmartCreationReviewNegativePatchSchema = z
  .object({
    text: z.string().trim().min(1).max(80).optional(),
    matchType: NegativeMatchTypeSchema.optional(),
    asin: z
      .string()
      .trim()
      .regex(/^[A-Z0-9]{10}$/)
      .optional(),
  })
  .strict()
  .refine(
    (patch) =>
      patch.text !== undefined || patch.matchType !== undefined || patch.asin !== undefined,
    { message: "Negative patch requires a value or match type change" },
  );

export const SmartCreationReviewChangeSchema = z
  .discriminatedUnion("operation", [
    z
      .object({
        operation: z.literal("add_campaign"),
        id: z.string().uuid(),
        sourceCampaignRef: SmartCreationReviewCampaignRefSchema,
        productSkus: z.array(z.string().trim().min(1).max(128)).min(1).max(500),
        name: NameSchema,
        dailyBudget: BudgetSchema,
        startingBid: BidSchema.optional(),
      })
      .strict(),
    z
      .object({
        operation: z.literal("set_campaign_budget_allocations"),
        allocations: z
          .array(
            z
              .object({
                ref: SmartCreationReviewCampaignRefSchema,
                allocation: SmartCreationReviewBudgetAllocationSchema,
              })
              .strict(),
          )
          .min(1)
          .max(SMART_CREATION_MAX_CAMPAIGNS),
      })
      .strict(),
    z
      .object({
        operation: z.literal("set_ai_action_policy"),
        policy: AiActionPolicyV1Schema,
      })
      .strict(),
    z
      .object({
        operation: z.literal("set_plan_schedule"),
        ref: SmartCreationReviewPlanRefSchema,
        startDate: CalendarDateSchema,
        endDate: CalendarDateSchema.nullable(),
      })
      .strict(),
    z
      .object({
        operation: z.literal("set_naming_rule"),
        ref: SmartCreationReviewPlanRefSchema,
        namingRule: SmartCreationReviewNamingRuleSchema,
      })
      .strict(),
    z
      .object({
        operation: z.literal("set_plan_name"),
        ref: SmartCreationReviewPlanRefSchema,
        name: NameSchema,
      })
      .strict(),
    z
      .object({
        operation: z.literal("set_portfolio"),
        ref: SmartCreationReviewPlanRefSchema,
        portfolioId: z.string().trim().min(1).max(128).nullable(),
      })
      .strict(),
    z
      .object({
        operation: z.literal("set_campaign_name"),
        ref: SmartCreationReviewCampaignRefSchema,
        name: NameSchema,
      })
      .strict(),
    z
      .object({
        operation: z.literal("set_campaign_budget_allocation"),
        ref: SmartCreationReviewCampaignRefSchema,
        allocation: SmartCreationReviewBudgetAllocationSchema,
      })
      .strict(),
    z
      .object({
        operation: z.literal("set_campaign_bidding_strategy"),
        ref: SmartCreationReviewCampaignRefSchema,
        biddingStrategy: BiddingStrategySchema,
      })
      .strict(),
    z
      .object({
        operation: z.literal("set_campaign_placement"),
        ref: SmartCreationReviewCampaignRefSchema,
        placement: PlacementSchema,
        percentage: PlacementPercentageSchema,
      })
      .strict(),
    z
      .object({
        operation: z.literal("remove_campaign_placement"),
        ref: SmartCreationReviewCampaignRefSchema,
        placement: PlacementSchema,
      })
      .strict(),
    z
      .object({
        operation: z.literal("set_campaign_excluded"),
        ref: SmartCreationReviewCampaignRefSchema,
        excluded: z.boolean(),
      })
      .strict(),
    z
      .object({
        operation: z.literal("set_ad_group_name"),
        ref: SmartCreationReviewAdGroupRefSchema,
        name: NameSchema,
      })
      .strict(),
    z
      .object({
        operation: z.literal("set_ad_group_default_bid"),
        ref: SmartCreationReviewAdGroupRefSchema,
        defaultBid: BidSchema,
      })
      .strict(),
    z
      .object({
        operation: z.literal("set_target_bid"),
        ref: SmartCreationReviewTargetRefSchema,
        bid: BidSchema,
      })
      .strict(),
    z
      .object({
        operation: z.literal("set_target_match_type"),
        ref: SmartCreationReviewTargetRefSchema,
        matchType: z.enum(["broad", "phrase", "exact"]),
      })
      .strict(),
    z
      .object({
        operation: z.literal("add_target"),
        adGroupRef: SmartCreationReviewAdGroupRefSchema,
        target: SmartCreationReviewTargetCreateSchema,
      })
      .strict(),
    z
      .object({ operation: z.literal("remove_target"), ref: SmartCreationReviewTargetRefSchema })
      .strict(),
    z
      .object({
        operation: z.literal("set_target_excluded"),
        ref: SmartCreationReviewTargetRefSchema,
        excluded: z.boolean(),
      })
      .strict(),
    z
      .object({
        operation: z.literal("add_negative"),
        adGroupRef: SmartCreationReviewAdGroupRefSchema,
        negative: SmartCreationReviewNegativeCreateSchema,
      })
      .strict(),
    z
      .object({
        operation: z.literal("update_negative"),
        ref: NegativeRefSchema,
        patch: SmartCreationReviewNegativePatchSchema,
      })
      .strict(),
    z.object({ operation: z.literal("remove_negative"), ref: NegativeRefSchema }).strict(),
  ])
  .superRefine((change, ctx) => {
    const keyword =
      change.operation === "add_negative" && change.negative.type === "keyword"
        ? change.negative
        : change.operation === "update_negative" && change.patch.text !== undefined
          ? {
              text: change.patch.text,
              matchType: change.patch.matchType ?? ("NEGATIVE_EXACT" as const),
            }
          : null;
    if (
      keyword &&
      !isSmartCreationNegativeKeywordWithinWordLimit(keyword.text, keyword.matchType)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: change.operation === "add_negative" ? ["negative", "text"] : ["patch", "text"],
        message: "Negative keyword exceeds its match type word limit",
      });
    }
    if (
      change.operation === "add_campaign" &&
      new Set(change.productSkus).size !== change.productSkus.length
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["productSkus"],
        message: "Product SKUs must be unique",
      });
    }
    if (
      change.operation === "set_plan_schedule" &&
      change.endDate &&
      change.endDate < change.startDate
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["endDate"],
        message: "endDate must be on or after startDate",
      });
    }
    if (change.operation === "set_campaign_budget_allocations") {
      if (
        new Set(change.allocations.map((item) => item.ref.lineageKey)).size !==
        change.allocations.length
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["allocations"],
          message: "Campaign allocations must be unique",
        });
      }
      if (new Set(change.allocations.map((item) => item.allocation.basis)).size !== 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["allocations"],
          message: "Campaign allocations must use the same basis",
        });
      }
    }
  });
export type SmartCreationReviewChange = z.infer<typeof SmartCreationReviewChangeSchema>;

/** Server-owned seed; commands cannot upload arbitrary Campaigns or Targets. */
export const SmartCreationReviewCampaignSeedSchema = z
  .object({
    draftContent: SmartCreationCampaignDraftContentV2Schema,
    graph: SmartCreationReviewGraphSchema,
  })
  .strict();
export type SmartCreationReviewCampaignSeed = z.infer<typeof SmartCreationReviewCampaignSeedSchema>;
